import React from 'react';

const OurStoryPage: React.FC = () => {
  const teamMembers = [
    {
      name: "Sarah Johnson",
      title: "CEO & Founder",
      bio: "With over 15 years in healthcare technology, Sarah founded Alert1 to provide seniors with the independence and safety they deserve.",
      image: "ceo"
    },
    {
      name: "Dr. Michael Chen",
      title: "Chief Medical Officer",
      bio: "Board-certified emergency physician with 20 years of experience in emergency response and senior care protocols.",
      image: "cmo"
    },
    {
      name: "Jennifer Martinez",
      title: "VP of Customer Care",
      bio: "Former nurse with a passion for senior advocacy, Jennifer ensures every customer receives compassionate, professional service.",
      image: "vp"
    },
    {
      name: "Robert Thompson",
      title: "Head of Technology",
      bio: "Technology veteran specializing in reliable communication systems and emergency response infrastructure.",
      image: "cto"
    }
  ];

  const milestones = [
    {
      year: "2010",
      title: "Company Founded",
      description: "Alert1 was established with a mission to keep seniors safe and independent in their own homes."
    },
    {
      year: "2013",
      title: "Mobile Technology Launch",
      description: "Introduced our first GPS-enabled mobile medical alert system for active seniors."
    },
    {
      year: "2016",
      title: "Fall Detection Innovation",
      description: "Pioneered advanced fall detection technology, automatically calling for help when needed."
    },
    {
      year: "2019",
      title: "100,000 Customers",
      description: "Reached the milestone of protecting over 100,000 seniors and their families nationwide."
    },
    {
      year: "2021",
      title: "Smart Watch Integration",
      description: "Launched our medical alert smartwatch, combining health monitoring with emergency response."
    },
    {
      year: "2024",
      title: "Leading the Industry",
      description: "Now protecting over 250,000 customers with 99.9% uptime and industry-leading response times."
    }
  ];

  const values = [
    {
      title: "Reliability",
      description: "Our systems maintain 99.9% uptime because your safety can't wait.",
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="25" fill="#4caf50"/>
          <path d="M20 30 L27 37 L40 24" stroke="white" strokeWidth="3" fill="none"/>
        </svg>
      )
    },
    {
      title: "Compassion",
      description: "Every interaction is handled with care, understanding, and respect.",
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M30 50 C30 50 10 35 10 22 C10 15 15 10 22 10 C26 10 30 13 30 13 C30 13 34 10 38 10 C45 10 50 15 50 22 C50 35 30 50 30 50 Z" fill="#d32f2f"/>
        </svg>
      )
    },
    {
      title: "Innovation",
      description: "We continuously improve our technology to better serve you.",
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="20" fill="#2196f3"/>
          <path d="M30 15 L33 25 L43 30 L33 35 L30 45 L27 35 L17 30 L27 25 Z" fill="white"/>
        </svg>
      )
    },
    {
      title: "Independence",
      description: "Empowering seniors to live confidently in their own homes.",
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <rect x="15" y="25" width="30" height="25" fill="#ff9800"/>
          <path d="M15 25 L30 10 L45 25" fill="#ff9800"/>
          <rect x="25" y="35" width="6" height="15" fill="white"/>
          <rect x="35" y="30" width="5" height="5" fill="white"/>
        </svg>
      )
    }
  ];

  const renderTeamMemberImage = (imageType: string) => {
    return (
      <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
        <circle cx="100" cy="100" r="90" fill="#f0f0f0"/>
        <circle cx="100" cy="80" r="30" fill="#d0d0d0"/>
        <path d="M40 160 Q100 120 160 160 L160 190 Q100 180 40 190 Z" fill="#d0d0d0"/>
        <text x="100" y="205" textAnchor="middle" fill="#666" fontSize="12" fontWeight="600">
          Professional Photo
        </text>
      </svg>
    );
  };

  return (
    <div className="our-story-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Our Story</h1>
            <p className="hero-subtitle">
              For over a decade, we've been dedicated to one mission: keeping seniors safe, 
              independent, and connected to the help they need, when they need it most.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
          <div className="mission-content">
            <div className="mission-text">
              <h2 className="section-title">Our Mission</h2>
              <p className="mission-statement">
                At Alert1, we believe that age should never limit independence. Our mission is to provide 
                seniors and their families with the most reliable, compassionate, and innovative medical 
                alert services available, ensuring peace of mind for everyone involved.
              </p>
              <div className="mission-stats">
                <div className="stat">
                  <span className="stat-number">250,000+</span>
                  <span className="stat-label">Customers Protected</span>
                </div>
                <div className="stat">
                  <span className="stat-number">99.9%</span>
                  <span className="stat-label">System Uptime</span>
                </div>
                <div className="stat">
                  <span className="stat-number">&lt;30s</span>
                  <span className="stat-label">Average Response</span>
                </div>
                <div className="stat">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">Monitoring</span>
                </div>
              </div>
            </div>
            <div className="mission-visual">
              <svg width="400" height="300" viewBox="0 0 400 300" fill="none">
                <circle cx="200" cy="150" r="100" fill="#f0f8ff" stroke="#d32f2f" strokeWidth="3"/>
                <circle cx="200" cy="150" r="60" fill="#d32f2f"/>
                <text x="200" y="140" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold">ALERT1</text>
                <text x="200" y="160" textAnchor="middle" fill="white" fontSize="12">PROTECTION</text>
                
                {/* Surrounding elements representing coverage */}
                <circle cx="120" cy="80" r="15" fill="#4caf50" opacity="0.8"/>
                <text x="120" y="85" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">HOME</text>
                
                <circle cx="280" cy="80" r="15" fill="#2196f3" opacity="0.8"/>
                <text x="280" y="85" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">MOBILE</text>
                
                <circle cx="120" cy="220" r="15" fill="#ff9800" opacity="0.8"/>
                <text x="120" y="225" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">FAMILY</text>
                
                <circle cx="280" cy="220" r="15" fill="#9c27b0" opacity="0.8"/>
                <text x="280" y="225" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">CARE</text>
                
                {/* Connection lines */}
                <path d="M135 85 L160 130" stroke="#4caf50" strokeWidth="2" opacity="0.6"/>
                <path d="M265 85 L240 130" stroke="#2196f3" strokeWidth="2" opacity="0.6"/>
                <path d="M135 215 L160 170" stroke="#ff9800" strokeWidth="2" opacity="0.6"/>
                <path d="M265 215 L240 170" stroke="#9c27b0" strokeWidth="2" opacity="0.6"/>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="container">
          <h2 className="section-title">Our Journey</h2>
          <div className="timeline">
            {milestones.map((milestone, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker">
                  <span className="timeline-year">{milestone.year}</span>
                </div>
                <div className="timeline-content">
                  <h3 className="timeline-title">{milestone.title}</h3>
                  <p className="timeline-description">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <h2 className="section-title">Our Values</h2>
          <p className="section-subtitle">
            These core principles guide everything we do at Alert1
          </p>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">
                  {value.icon}
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <h2 className="section-title">Meet Our Leadership Team</h2>
          <p className="section-subtitle">
            Experienced professionals dedicated to your safety and peace of mind
          </p>
          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <div key={index} className="team-card">
                <div className="team-photo">
                  {renderTeamMemberImage(member.image)}
                </div>
                <div className="team-info">
                  <h3 className="team-name">{member.name}</h3>
                  <p className="team-title">{member.title}</p>
                  <p className="team-bio">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recognition Section */}
      <section className="recognition-section">
        <div className="container">
          <h2 className="section-title">Industry Recognition</h2>
          <div className="awards-grid">
            <div className="award-card">
              <div className="award-icon">🏆</div>
              <h3>Best Medical Alert Service 2023</h3>
              <p>Senior Living Magazine</p>
            </div>
            <div className="award-card">
              <div className="award-icon">⭐</div>
              <h3>A+ Rating</h3>
              <p>BBB Accredited Business</p>
            </div>
            <div className="award-card">
              <div className="award-icon">🛡️</div>
              <h3>HIPAA Compliant</h3>
              <p>Healthcare Data Security</p>
            </div>
            <div className="award-card">
              <div className="award-icon">📞</div>
              <h3>Excellence in Customer Service</h3>
              <p>National Senior Care Association</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Join the Alert1 Family</h2>
            <p className="cta-subtitle">
              Experience the peace of mind that comes from knowing help is always just a button press away.
            </p>
            <div className="cta-buttons">
              <button className="cta-button primary large">
                Call Now: 1-800-693-5433
              </button>
              <button className="cta-button secondary large">
                View Our Systems
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurStoryPage;
